import React from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  Divider,
  TextField,
  CircularProgress,
  Button,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function FilterSideBarUI({
  brands,
  categoryFeatures,
  brandSearchQuery,
  onBrandSearchChange,
  localFilters,
  filteredBrands,
  hasActiveFilters,
  previewCount,
  loadingCount,
  onDiscountChange,
  onBrandChange,
  onFeatureChange,
  isFeatureChecked,
  onPriceChange,
  onApply,
  onClear,
  formatNumberWithCommas,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flex: 1,
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      {/* Scrollable Filter Controls */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          gap: 1,
          px: 1.5,
          pt: 1.5,
          pb: 4,
        }}
      >
        {/* Clear Filters Bar */}
        <Collapse in={hasActiveFilters} timeout={250} unmountOnExit>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 1.5,
              py: 1,
              bgcolor: "action.hover",
              borderRadius: 1.5,
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              فیلترهای انتخاب‌شده
            </Typography>
            <Button
              variant="text"
              color="error"
              size="small"
              onClick={onClear}
              sx={{
                fontSize: "0.78rem",
                fontWeight: 700,
                p: 0,
                minWidth: "auto",
                "&:hover": {
                  bgcolor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              حذف همه
            </Button>
          </Box>
        </Collapse>

        {/* Discount Switch */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            px: 0.5,
            width: "100%",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            فقط کالاهای تخفیف‌دار
          </Typography>
          <Switch
            color="error"
            checked={!!localFilters.has_discount}
            onChange={onDiscountChange}
          />
        </Box>

        <Divider sx={{ my: 0.5 }} />

        {/* Brands Accordion */}
        {brands.length > 0 && (
          <>
            <Accordion
              disableGutters
              elevation={0}
              defaultExpanded
              sx={{
                width: "100%",
                "&:before": { display: "none" },
                bgcolor: "transparent",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: 0.5, width: "100%" }}
              >
                <Typography variant="body2" fontWeight={700}>
                  برند
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 1 }}>
                <TextField
                  size="small"
                  placeholder="جستجوی برند..."
                  value={brandSearchQuery}
                  onChange={(e) => onBrandSearchChange(e.target.value)}
                  fullWidth
                  sx={{ mb: 1.5 }}
                  inputProps={{ style: { textAlign: "right" } }}
                />

                {filteredBrands.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 1, textAlign: "center" }}
                  >
                    برندی یافت نشد
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      maxHeight: 180,
                      overflowY: "auto",
                      pr: 0.5,
                      pl: 0.5,
                      "&::-webkit-scrollbar": { width: "5px" },
                      "&::-webkit-scrollbar-thumb": {
                        background: "grey.300",
                        borderRadius: "4px",
                      },
                    }}
                  >
                    <FormGroup
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        gap: 0.5,
                      }}
                    >
                      {filteredBrands.map((brand) => {
                        const selectedBrands = localFilters.brand
                          ? String(localFilters.brand).split(",")
                          : [];
                        const isChecked = selectedBrands.includes(brand.slug);

                        return (
                          <FormControlLabel
                            key={brand.id || brand.slug}
                            control={
                              <Checkbox
                                size="small"
                                checked={isChecked}
                                onChange={() => onBrandChange(brand.slug)}
                                sx={{
                                  color: "grey.400",
                                  "&.Mui-checked": { color: "#ef4056" },
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {brand.name}
                              </Typography>
                            }
                            sx={{
                              width: "100%",
                              m: 0,
                              justifyContent: "flex-start",
                            }}
                          />
                        );
                      })}
                    </FormGroup>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
            <Divider sx={{ my: 0.5 }} />
          </>
        )}

        {/* Dynamic Category Features Accordions */}
        {categoryFeatures.map((feat) => (
          <React.Fragment key={feat.name}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                width: "100%",
                "&:before": { display: "none" },
                bgcolor: "transparent",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: 0.5, width: "100%" }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {feat.name}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 1 }}>
                <Box
                  sx={{
                    maxHeight: 180,
                    overflowY: "auto",
                    pr: 0.5,
                    pl: 0.5,
                    "&::-webkit-scrollbar": { width: "5px" },
                    "&::-webkit-scrollbar-thumb": {
                      background: "grey.300",
                      borderRadius: "4px",
                    },
                  }}
                >
                  <FormGroup
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      gap: 0.5,
                    }}
                  >
                    {feat.options.map((opt) => (
                      <FormControlLabel
                        key={opt}
                        control={
                          <Checkbox
                            size="small"
                            checked={isFeatureChecked(feat.name, opt)}
                            onChange={() => onFeatureChange(feat.name, opt)}
                            sx={{
                              color: "grey.400",
                              "&.Mui-checked": { color: "#ef4056" },
                            }}
                          />
                        }
                        label={<Typography variant="body2">{opt}</Typography>}
                        sx={{
                          width: "100%",
                          m: 0,
                          justifyContent: "flex-start",
                        }}
                      />
                    ))}
                  </FormGroup>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Divider sx={{ my: 0.5 }} />
          </React.Fragment>
        ))}

        {/* Price Accordion */}
        <Accordion
          disableGutters
          elevation={0}
          defaultExpanded
          sx={{
            width: "100%",
            "&:before": { display: "none" },
            bgcolor: "transparent",
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ px: 0.5, width: "100%" }}
          >
            <Typography variant="body2" fontWeight={700}>
              قیمت (تومان)
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              px: 0.5,
              pt: 0,
              pb: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <TextField
              placeholder="از قیمت"
              size="small"
              value={formatNumberWithCommas(localFilters.min_price)}
              onChange={(e) => onPriceChange("min_price", e)}
              fullWidth
              sx={{
                "& input": { textAlign: "right" },
                "& input::placeholder": { textAlign: "right", opacity: 0.8 },
              }}
            />
            <TextField
              placeholder="تا قیمت"
              size="small"
              value={formatNumberWithCommas(localFilters.max_price)}
              onChange={(e) => onPriceChange("max_price", e)}
              fullWidth
              sx={{
                "& input": { textAlign: "right" },
                "& input::placeholder": { textAlign: "right", opacity: 0.8 },
              }}
            />
          </AccordionDetails>
        </Accordion>
      </Box>

      {/* Sticky Action Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          flexShrink: 0,
          zIndex: 10,
          boxShadow: "0px -4px 16px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Button
          variant="contained"
          color="error"
          fullWidth
          onClick={onApply}
          disabled={loadingCount}
          sx={{
            py: 1.2,
            height: 48,
            fontWeight: 700,
            fontSize: "0.95rem",
            borderRadius: 2,
            boxShadow: "0px 4px 12px rgba(239, 64, 86, 0.3)",
            "&:hover": {
              boxShadow: "0px 6px 16px rgba(239, 64, 86, 0.4)",
            },
          }}
        >
          {loadingCount ? (
            <CircularProgress size={24} color="inherit" />
          ) : previewCount !== null ? (
            `مشاهده ${previewCount.toLocaleString("fa-IR")} نتیجه`
          ) : (
            "مشاهده نتایج"
          )}
        </Button>
      </Box>
    </Box>
  );
}
